import AppLayout from '@/layout/AppLayout.vue';
import { createRouter, createWebHistory } from 'vue-router';
import { supabase } from '../supabase'
import { useUserStore } from '@/stores/user'
import { useOnboardingStore } from '@/stores/onboarding'
import { clearOnboardingPath, trackOnboardingNavigation } from '@/composables/onboardingPath.mjs'

const routes = [
    {
        path: '/',
        component: AppLayout,
        children: [
            {
                path: '/dashboard',
                name: 'dashboard',
                component: () => import('@/views/Dashboard.vue')
            },
            {
                path: '/profile',
                name: 'profile',
                component: () => import('@/views/pages/Profile.vue')
            },
            {
                path: '/accounts',
                name: 'accounts',
                component: () => import('@/views/pages/BankAccounts.vue')
            },
            {
                path: '/transactions',
                name: 'transactions',
                component: () => import('@/views/pages/Transactions.vue')
            },
            {
                path: '/budget',
                name: 'budget',
                component: () => import('@/views/pages/Budget.vue')
            },
            {
                path: '/reports',
                name: 'reports',
                component: () => import('@/views/pages/Reports.vue')
            },
            {
                path: '/members',
                name: 'members',
                component: () => import('@/views/pages/Members.vue')
            },
            {
                path: '/dues',
                name: 'dues',
                component: () => import('@/views/pages/Dues.vue')
            },
            {
                path: '/invoices',
                name: 'invoices',
                component: () => import('@/views/pages/Invoices.vue')
            },
            {
                path: '/',
                name: 'home',
                component: () => import('@/views/pages/Onboarding.vue')
            },
            {
                path: '/onboarding',
                name: 'onboarding',
                component: () => import('@/views/pages/Onboarding.vue')
            }
        ]
    },
    {
        path: '/pages/notfound',
        name: 'notfound',
        component: () => import('@/views/pages/NotFound.vue')
    },

    {
        path: '/auth/login',
        name: 'login',
        component: () => import('@/views/pages/auth/Login.vue'),
        props: { mode: 'login' }
    },
    {
        path: '/auth/signup',
        name: 'signup',
        component: () => import('@/views/pages/auth/Login.vue'),
        props: { mode: 'signup' }
    },
    {
        path: '/auth/start',
        name: 'start',
        component: () => import('@/views/pages/auth/SignupStart.vue')
    },
    {
        path: '/auth/verify',
        name: 'verify',
        component: () => import('@/views/pages/auth/Verify.vue')
    },
    {
        path: '/checkout',
        name: 'checkout',
        component: () => import('@/views/pages/Checkout.vue')
    },
    {
        path: '/auth/logout',
        name: 'logout',
        component: () => import('@/views/pages/auth/Logout.vue')
    },
]
const router = createRouter({
    history: createWebHistory(),
    routes
});

router.beforeEach(async (to, from) => {
    trackOnboardingNavigation(from.path, to.path)

    // Don't redirect away from the login page
    if (to.name === 'login') {
        return true
    }

    // Don't redirect away from the signup page
    if (to.name === 'signup') {
        return true
    }

    // Don't redirect away from the simplified signup page
    if (to.name === 'start') {
        return true
    }

    // Don't redirect away from the logout page
    if (to.name === 'logout') {
        return true
    }

    // Redirect to signup if not authenticated
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
        return { name: 'signup' }
    }
    if (!session.user) {
        return { name: 'signup'}
    }

    // Ensure trial is initialized before checking subscription status
    const allowedWhenBlocked = ['checkout', 'profile', 'login', 'signup', 'logout', 'verify']
    const userStore = useUserStore()
    if (!userStore.trialInitialized) {
        await userStore.initializeTrial()
    }
    if (!userStore.subscriptionStatusChecked) {
        await userStore.checkSubscriptionStatus()
    }
    if (userStore.subscriptionBlocked && !allowedWhenBlocked.includes(to.name)) {
        return { name: 'checkout' }
    }

    if (to.name === 'home') {
        const onboardingStore = useOnboardingStore()
        try {
            await onboardingStore.ensureLoaded()
            if (onboardingStore.isGrandfathered) {
                clearOnboardingPath()
                return { name: 'dashboard' }
            }
            await onboardingStore.refreshAllProgress()
            if (onboardingStore.isAccountingComplete && onboardingStore.isDuesComplete) {
                return { name: 'dashboard' }
            }
        } catch (error) {
            console.error('Could not load onboarding progress for home:', error)
        }
        return { name: 'onboarding' }
    }

    // Route brand-new accounts into the guided onboarding flow. This fires at
    // most once per account (when the onboarding row is first created); after
    // that, users with incomplete setup get the dashboard resume banner instead
    // of a forced redirect.
    // Verification must mount first to finish signup attribution and aliasing.
    if (to.name !== 'onboarding' && to.name !== 'verify' && !userStore.subscriptionBlocked) {
        const onboardingStore = useOnboardingStore()
        await onboardingStore.ensureLoaded()
        if (onboardingStore.redirectToWizard) {
            onboardingStore.redirectToWizard = false
            return { name: 'onboarding' }
        }
    }

});

export default router;
